// 每日目標與每日複習任務
// 儲存：登入者 users/{uid}/daily/{YYYY-MM-DD}（count、taskDone）與 users/{uid}/daily/goal（n）；訪客存 localStorage
import { doc, getDoc, setDoc, increment } from 'firebase/firestore';
import { db } from './firebase';
import type { AnswerMap, QRef } from './answerLog';

export const GOAL_OPTIONS = [10, 20, 30];
export const DEFAULT_GOAL = 20;
export const TASK_SIZE = 10;                 // 每日複習任務題數
export const STALE_DAYS = 7;                 // 超過幾天沒練，算「好久沒練」

export type Daily = { date: string; count: number; taskDone: boolean; goal: number };

// 本地日期 YYYY-MM-DD（跨午夜即換日）
export const dateKey = (t = Date.now()) => {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const LS_DAILY = 'gsat_daily';   // { goal, days: { 'YYYY-MM-DD': { count, taskDone } } }
type Local = { goal?: number; days?: Record<string, { count?: number; taskDone?: boolean }> };
const readLocal = (): Local => { try { return JSON.parse(localStorage.getItem(LS_DAILY) || '{}'); } catch { return {}; } };
const writeLocal = (v: Local) => { try { localStorage.setItem(LS_DAILY, JSON.stringify(v)); } catch {} };

export async function loadDaily(uid: string | null, date = dateKey()): Promise<Daily> {
  if (!uid) {
    const l = readLocal();
    return { date, count: l.days?.[date]?.count ?? 0, taskDone: !!l.days?.[date]?.taskDone, goal: l.goal ?? DEFAULT_GOAL };
  }
  const [day, goal] = await Promise.all([getDoc(doc(db, 'users', uid, 'daily', date)), getDoc(doc(db, 'users', uid, 'daily', 'goal'))]);
  return { date, count: day.data()?.count ?? 0, taskDone: !!day.data()?.taskDone, goal: goal.data()?.n ?? DEFAULT_GOAL };
}

export async function bumpDaily(uid: string | null, date: string) {
  if (!uid) {
    const l = readLocal();
    const days = { ...l.days, [date]: { ...l.days?.[date], count: (l.days?.[date]?.count ?? 0) + 1 } };
    writeLocal({ ...l, days });
    return;
  }
  await setDoc(doc(db, 'users', uid, 'daily', date), { count: increment(1) }, { merge: true });
}

export async function saveGoal(uid: string | null, n: number) {
  if (!uid) { writeLocal({ ...readLocal(), goal: n }); return; }
  await setDoc(doc(db, 'users', uid, 'daily', 'goal'), { n });
}

export async function saveTaskDone(uid: string | null, date: string) {
  if (!uid) {
    const l = readLocal();
    writeLocal({ ...l, days: { ...l.days, [date]: { ...l.days?.[date], taskDone: true } } });
    return;
  }
  await setDoc(doc(db, 'users', uid, 'daily', date), { taskDone: true }, { merge: true });
}

// 挑今日任務題目：先錯題本，再「作答過、超過 STALE_DAYS 天沒練」的題目（曾答錯者優先、越久沒練越優先）
// wrongRefs：錯題本中有題目位置的題目；answers：逐題作答紀錄；ok：只挑這些科目的題目（例如訪客只能讀開放科目）
export function pickDailyTask(
  wrongRefs: QRef[], answers: AnswerMap, now: number, size = TASK_SIZE, ok: (s: string) => boolean = () => true,
): { refs: QRef[]; wrong: number; stale: number } {
  const key = (r: QRef) => `${r.s}__${r.t}__${r.id}`;
  const chosen = new Map<string, QRef>();
  for (const r of wrongRefs) {
    if (chosen.size >= size) break;
    if (ok(r.s)) chosen.set(key(r), { s: r.s, t: r.t, id: r.id });
  }
  const wrong = chosen.size;
  const cutoff = now - STALE_DAYS * 86400000;
  const stale = Object.values(answers)
    .filter(a => a.ts && a.ts < cutoff && ok(a.s) && !chosen.has(key(a)))
    .sort((x, y) => (y.wrongs > 0 ? 1 : 0) - (x.wrongs > 0 ? 1 : 0) || (x.ts ?? 0) - (y.ts ?? 0));
  for (const a of stale) {
    if (chosen.size >= size) break;
    chosen.set(key(a), { s: a.s, t: a.t, id: a.id });
  }
  return { refs: Array.from(chosen.values()), wrong, stale: chosen.size - wrong };
}
